import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import "./fts-back-in-stock.js";

const RADIO_SELECTOR = 'input[name^="kaching-bundles-deal"]';
const INITIALIZED_EVENT = "kaching-bundles-initialized";

/** Keep the editorial sidebar bounded by the sections before the native PDP. */
class AdvertorialLayout extends Component {
  #observer;
  #controller;
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.addEventListener("click", this.#jumpToOffer, {
      signal: this.#controller.signal,
    });
    const article = this.querySelector(".fts-advertorial-article");
    if (!article) return;
    const update = () => {
      const sections = [...article.children];
      const index = sections.findIndex((section) =>
        section.querySelector("embedded-product"),
      );
      if (index >= 0)
        this.style.setProperty("--story-end-row", String(index + 1));
    };
    update();
    this.#observer = new MutationObserver(update);
    this.#observer.observe(article, { childList: true });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  /** Account for the header that will appear when jumping back up the page. */
  #jumpToOffer = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    )
      return;
    const link = event.target.closest('a[href="#founder-offer"]');
    const offer = this.querySelector("#founder-offer");
    if (!link || !offer || link.target === "_blank") return;

    const header = document.querySelector("#header-component");
    const top = offer.getBoundingClientRect().top;
    const sticky = header?.getAttribute("sticky");
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const headerVisible =
      sticky === "always" ||
      (sticky === "scroll-up" &&
        (top < 16 ||
          (top <= headerHeight + 16 &&
            header.dataset.stickyState === "active")));
    const offset = 16 + (headerVisible ? headerHeight : 0);
    event.preventDefault();
    if (window.location.hash !== "#founder-offer")
      window.history.pushState(null, "", link.href);
    offer.setAttribute("tabindex", "-1");
    offer.focus({ preventScroll: true });
    window.scrollTo({
      top: Math.max(0, window.scrollY + top - offset),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  };
}

/**
 * Page-context bridge around the existing PDP. Never selects an app offer or
 * writes its quantities, discounts, options, or gifts. The native components
 * render the entire purchase UI; this bridge only guards availability and
 * applies the shared preorder notice to the native button.
 */
class EmbeddedProduct extends Component {
  #controller;
  #observer;
  #timer;
  #frame;
  #data;
  #ready = false;
  #valid = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#ready = false;
    this.#valid = false;
    try {
      this.#data = JSON.parse(
        this.querySelector("[data-embedded-variants]").textContent,
      );
    } catch {
      return;
    }
    const { signal } = this.#controller;
    this.addEventListener("change", this.#scheduleSync, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#scheduleSync, {
      signal,
    });
    this.addEventListener("click", this.#guardClick, { capture: true, signal });
    this.addEventListener("submit", this.#guardSubmit, {
      capture: true,
      signal,
    });
    window.addEventListener(INITIALIZED_EVENT, this.#watchWidget, { signal });
    this.#watchWidget();
    this.#timer = window.setTimeout(() => {
      if (!this.#ready) {
        this.#observer?.disconnect();
        this.#showFallback(true);
      }
    }, 5000);

    const requested = new URL(window.location.href).searchParams.get("variant");
    if (
      this.#data.variants.some((variant) => String(variant.id) === requested)
    ) {
      customElements.whenDefined("variant-picker").then(() => {
        if (signal.aborted) return;
        const picker = this.querySelector(
          'variant-picker[ref="mainVariantPicker"]',
        );
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set("variant", requested);
        // Use the native picker/render lifecycle; do not manipulate Kaching controls.
        picker?.fetchUpdatedSection(url.toString());
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    window.clearTimeout(this.#timer);
    window.cancelAnimationFrame(this.#frame);
  }

  #watchWidget = () => {
    if (!this.isConnected) return;
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(this.#scheduleSync);
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-value", "checked", "deal-block"],
    });
    this.#scheduleSync();
  };

  #scheduleSync = () => {
    window.cancelAnimationFrame(this.#frame);
    this.#frame = window.requestAnimationFrame(() => this.#sync());
  };

  #selection() {
    const radio = this.querySelector(`${RADIO_SELECTOR}:checked`);
    const widget = radio?.closest("kaching-bundles-block[deal-block]");
    if (!widget) return null;
    let config;
    try {
      config = JSON.parse(widget.getAttribute("deal-block"));
    } catch {
      return null;
    }
    const quantity = Number(
      config.dealBars?.find((bar) => String(bar.id) === radio.value)?.quantity,
    );
    if (!Number.isInteger(quantity) || quantity < 1) return null;
    const rows = [
      ...radio
        .closest(".kaching-bundles__bar")
        .querySelectorAll(".kaching-bundles__bar-variant"),
    ].slice(0, quantity);
    const variants = rows.map((row) => {
      const options = this.#data.options.map((name) => {
        const control = [
          ...row.querySelectorAll("[data-name][data-value]"),
        ].find((element) => element.dataset.name === name);
        return control instanceof HTMLSelectElement
          ? control.value
          : control?.dataset.value;
      });
      return this.#data.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index]),
      );
    });
    return { variants, complete: variants.length === quantity };
  }

  #sync() {
    const selection = this.#selection();
    this.#ready = Boolean(selection?.complete);
    this.#valid =
      this.#ready && selection.variants.every((variant) => variant?.available);
    if (this.#ready) this.#showFallback(false);
    const button = this.querySelector(
      'product-form-component [ref="addToCartButton"]',
    );
    if (!button) return;
    const isBackInStockTrigger = this.#isBackInStockTrigger(button);
    button.disabled = !this.#valid && !isBackInStockTrigger;
    const shipping = [
      ...new Set(
        selection?.variants
          .filter(Boolean)
          .map((variant) => variant.shipping)
          .filter(Boolean) ?? [],
      ),
    ].join("; ");
    const label = button.querySelector(".add-to-cart-text__content");
    const text = isBackInStockTrigger
      ? "Notify me when available"
      : !this.#ready
        ? "Choose your bundle"
        : !this.#valid
          ? "Sold out"
          : shipping
            ? "Pre-Order Now"
            : "Add to cart";
    if (label && label.textContent !== text) label.textContent = text;
    const content = button.querySelector(".add-to-cart-text");
    let secondary = button.querySelector(".add-to-cart-text__secondary");
    if (shipping && this.#valid && content) {
      if (!secondary) {
        secondary = document.createElement("span");
        secondary.className = "add-to-cart-text__secondary";
        content.append(secondary);
      }
      if (secondary.textContent !== shipping) secondary.textContent = shipping;
    } else secondary?.remove();
    content?.classList.toggle(
      "add-to-cart-text--has-secondary",
      Boolean(shipping && this.#valid),
    );
    const wrapper = button.closest(".buy-buttons-block");
    wrapper?.classList.toggle(
      "buy-buttons-block--preorder",
      Boolean(shipping && this.#valid),
    );
    if (wrapper)
      wrapper.dataset.pdpPreorder = String(Boolean(shipping && this.#valid));
  }

  #showFallback(show) {
    const fallback = this.querySelector("[data-embedded-fallback]");
    if (fallback) fallback.hidden = !show;
  }

  /** Validate synchronously before the native form starts a cart request. */
  canSubmitPurchase() {
    if (!this.#data) return false;
    this.#sync();
    return this.#ready && this.#valid;
  }

  #guardSubmit = (event) => {
    if (
      event.target.closest("product-form-component") !==
      this.querySelector("product-form-component")
    )
      return;
    if (!this.canSubmitPurchase()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #guardClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[ref="addToCartButton"]');
    // A notification click does not purchase anything. Submissions still go
    // through the availability guard, including programmatic submissions.
    if (!button) return;
    if (this.#isBackInStockTrigger(button)) {
      const popup = this.querySelector("fts-back-in-stock");
      if (!popup) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const unavailable =
        this.#selection()?.variants.filter(
          (variant) => variant?.available === false,
        ) ?? [];
      const nativeId = this.querySelector(
        'product-form-component [ref="variantId"]',
      )?.value;
      const nativeVariant = this.#data.variants.find(
        (variant) => String(variant.id) === nativeId && !variant.available,
      );
      popup.show(
        unavailable.length ? unavailable : nativeVariant ? [nativeVariant] : [],
      );
      return;
    }
    this.#guardSubmit(event);
  };

  #isBackInStockTrigger(button) {
    return (
      button.getAttribute("type") === "button" &&
      button.classList.contains("klaviyo-bis-trigger")
    );
  }
}

if (!customElements.get("fts-advertorial-layout"))
  customElements.define("fts-advertorial-layout", AdvertorialLayout);
if (!customElements.get("embedded-product"))
  customElements.define("embedded-product", EmbeddedProduct);
